import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { Currency } from '../../api/stripe';
import { useTranslation } from 'react-i18next';
import { CabinetPreviewFrame, cabinetPreviewLangForPage } from '../../components/CabinetPreviewFrame/CabinetPreviewFrame';
import './index.css';
import { BookCover } from '../../components/BookCover/BookCover';
import { BookPage } from '../../components/BookPage/BookPage';
import { LangPicker } from './LangPicker';
import { FALLBACK_LANGUAGE } from '../../i18n/languages';
import { CheckoutSidebar } from '../../components/Checkout/CheckoutSidebar';
import { isAskVariant, isMirrorVariant, mainAppUrl } from '../../utils/variant';
import { trackLead } from '../../utils/pixel';

import heroVideo from '../../assets/landing/people/video.mp4';
import heroVideoPoster from '../../assets/landing/people/preview.jpg';
import p1 from '../../assets/landing/people/IMG_7248.PNG';
import p3 from '../../assets/landing/people/IMG_7251.PNG';
import p7 from '../../assets/landing/people/IMG_7256.PNG';
import p9 from '../../assets/landing/people/IMG_7258.PNG';
import p11 from '../../assets/landing/people/IMG_7260.PNG';
import p13 from '../../assets/landing/people/IMG_7262.PNG';
import p15 from '../../assets/landing/people/IMG_4758.PNG';

import packageImage from '../../assets/landing/package.jpg';
import logoSvg from '../../assets/logo.svg';

import cover1 from '../../assets/landing/cmock1.jpg';
import cover2 from '../../assets/landing/cmock2.jpg';
import cover3 from '../../assets/landing/cmock3.jpg';
import cover4 from '../../assets/landing/cmock4.jpg';
import cover5 from '../../assets/landing/cmock5.jpg';

import npMom from '../../assets/landing/newpeople/маме.jpg';
import npBoyfriend from '../../assets/landing/newpeople/парню.jpg';
import npGirlfriend from '../../assets/landing/newpeople/девушке.jpg';
import npWife from '../../assets/landing/newpeople/жене.jpg';
import npHusband from '../../assets/landing/newpeople/мужу.jpg';
import npFriend from '../../assets/landing/people/IMG_8434.PNG';
import npSister from '../../assets/landing/newpeople/сестре.jpg';
import npGrandma from '../../assets/landing/newpeople/бабушке.jpg';

import inner1 from '../../assets/innerbooks/inner1.jpg';
import inner2 from '../../assets/innerbooks/inner2.jpg';
import inner3 from '../../assets/innerbooks/inner3.jpg';
import inner4 from '../../assets/innerbooks/inner4.jpg';
import inner5 from '../../assets/innerbooks/inner5.jpg';

import vb1 from '../../assets/landing/videoblock/1.mp4';
import vb2 from '../../assets/landing/videoblock/2.mp4';
import vb3 from '../../assets/landing/videoblock/3.mp4';
import vb4 from '../../assets/landing/videoblock/4.mp4';
import vb5 from '../../assets/landing/videoblock/5.mp4';
import vb6 from '../../assets/landing/videoblock/6.mp4';
import vb7 from '../../assets/landing/videoblock/7.mp4';
import vbp1 from '../../assets/landing/videoblock/poster1.jpg';
import vbp2 from '../../assets/landing/videoblock/poster2.jpg';
import vbp3 from '../../assets/landing/videoblock/poster3.jpg';
import vbp4 from '../../assets/landing/videoblock/poster4.jpg';
import vbp5 from '../../assets/landing/videoblock/poster5.jpg';
import vbp6 from '../../assets/landing/videoblock/poster6.jpg';
import vbp7 from '../../assets/landing/videoblock/poster7.jpg';

// Обложки, циклящиеся вместе с occIdx в .lp-cta-блоке
// (день рождения, годовщина, день матери, праздник).
const bookCovers = [cover5, cover3, cover2, cover1, cover4];

// Review slot order — quote/by content for the three text reviews come
// from translations (reviews.r1Quote / r1By / r2Quote / ...).
type ReviewSlot =
  | { kind: 'photo'; poster: string }
  | { kind: 'text'; quoteKey: string; byKey: string };

// Порядок такой, чтобы при slot-распределении по i % 3 каждая из
// трёх колонн получала минимум один текст. Это особенно важно на
// мобилке, где третья колонна скрыта, но мы всё ещё хотим видеть
// текстовые отзывы в оставшихся двух.
const reviewSlots: ReadonlyArray<ReviewSlot> = [
  { kind: 'text',  quoteKey: 'reviews.r1Quote', byKey: 'reviews.r1By' }, // 0 → col 0
  { kind: 'photo', poster: p1 },                                          // 1 → col 1
  { kind: 'photo', poster: p3 },                                          // 2 → col 2
  { kind: 'photo', poster: p7 },                                          // 3 → col 0
  { kind: 'text',  quoteKey: 'reviews.r2Quote', byKey: 'reviews.r2By' }, // 4 → col 1
  { kind: 'photo', poster: p9 },                                          // 5 → col 2
  { kind: 'photo', poster: p11 },                                         // 6 → col 0
  { kind: 'photo', poster: p13 },                                         // 7 → col 1
  { kind: 'text',  quoteKey: 'reviews.r3Quote', byKey: 'reviews.r3By' }, // 8 → col 2
  { kind: 'photo', poster: p15 },                                         // 9 → col 0
];

// «Книга изнутри» (как на aa) — пять квадратных разворотов с пояснением.
// Текст приходит из переводов (см. inner.* в landing.json).
const innerSlides: ReadonlyArray<{ src: string; titleKey: string; bodyKey: string }> = [
  { src: inner1, titleKey: 'inner.s1Title', bodyKey: 'inner.s1Body' },
  { src: inner2, titleKey: 'inner.s2Title', bodyKey: 'inner.s2Body' },
  { src: inner3, titleKey: 'inner.s3Title', bodyKey: 'inner.s3Body' },
  { src: inner4, titleKey: 'inner.s4Title', bodyKey: 'inner.s4Body' },
  { src: inner5, titleKey: 'inner.s5Title', bodyKey: 'inner.s5Body' },
];

// «Слова тех, кто уже подарил книгу» (как на aa) — вертикальные
// видео-отзывы 9:16, каждый ведёт на свой Instagram-reel. poster — первый
// кадр ролика: показывается мгновенно, даже если видео ещё не начало
// грузиться (iOS не префетчит видео до play()).
const videoReels: ReadonlyArray<{ src: string; poster: string; href: string }> = [
  { src: vb1, poster: vbp1, href: 'https://www.instagram.com/reels/DanaTpmMM_R/' },
  { src: vb2, poster: vbp2, href: 'https://www.instagram.com/reels/DaFquH_gk6P/' },
  { src: vb3, poster: vbp3, href: 'https://www.instagram.com/reels/DaK0GBGgG6u/' },
  { src: vb4, poster: vbp4, href: 'https://www.instagram.com/reels/DZX816RgAvF/' },
  { src: vb5, poster: vbp5, href: 'https://www.instagram.com/reels/DaDHjmggMsp/' },
  { src: vb6, poster: vbp6, href: 'https://www.instagram.com/reels/Daza3u1temF/' },
  { src: vb7, poster: vbp7, href: 'https://www.instagram.com/reels/DZXoAWbAf7x/' },
];

const ctaOccasionKeys = [
  { wordKey: 'cta.birthdayWord',     msgKey: 'cta.birthdayMsg' },
  { wordKey: 'cta.anniversaryWord',  msgKey: 'cta.anniversaryMsg' },
  { wordKey: 'cta.mothersWord',      msgKey: 'cta.mothersMsg' },
  { wordKey: 'cta.holidayWord',      msgKey: 'cta.holidayMsg' },
];

// На этом зеркале все CTA ведут в Telegram-канал менеджера — единственный
// канал связи. Константа на случай, если адрес поменяется — поправить
// в одном месте.
const TELEGRAM_LINK = 'https://t.me/lovestoriasales';

// Иконка Telegram. Без обёртки — рисуется в контексте кнопки
// order-modal'а, цвет наследуется через currentColor.
const TelegramIcon: React.FC = () => (
  <svg
    className="lp-msg-icon"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M21.94 4.34a1.2 1.2 0 0 0-1.62-1.16L2.7 9.95c-1.07.4-1.06 1.05-.18 1.31l4.5 1.4 10.45-6.6c.49-.31.94-.14.57.2L9.6 13.97l-.33 4.86c.42 0 .6-.19.84-.42l2.02-1.96 4.2 3.1c.77.42 1.32.21 1.51-.7l2.74-12.78c.08-.39.05-.66-.04-.83Z" />
  </svg>
);


const Landing: React.FC = () => {
  const { t, i18n } = useTranslation('landing');
  const { lang: urlLang } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  // ?curr=KZT — preselect a currency in the checkout sidebar (default USD).
  const SUPPORTED_CURRENCIES = ['USD', 'AZN', 'BYN', 'GEL', 'KZT', 'TRY'] as const;
  const currParam = searchParams.get('curr')?.toUpperCase();
  const defaultCurrency: Currency =
    currParam && (SUPPORTED_CURRENCIES as ReadonlyArray<string>).includes(currParam)
      ? (currParam as Currency)
      : 'USD';
  const langPrefix = '/' + (urlLang || FALLBACK_LANGUAGE);
  const [occIdx, setOccIdx] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [innerIdx, setInnerIdx] = useState(0);
  const navRef = useRef<HTMLElement>(null);

  const innerPrev = () =>
    setInnerIdx((i) => (i - 1 + innerSlides.length) % innerSlides.length);
  const innerNext = () =>
    setInnerIdx((i) => (i + 1) % innerSlides.length);

  // Ленивая загрузка видео-отзывов: ролики по ~1-3 МБ, поэтому src
  // вешается только когда секция подъехала к вьюпорту (~600px запаса).
  // Дальше каждый ролик независимо: как только первый кадр декодирован
  // (loadeddata) — карточка убирает шиммер, показывает кадр и стартует.
  const videosRef = useRef<HTMLElement>(null);
  const [videosArmed, setVideosArmed] = useState(false);
  const [readyReels, setReadyReels] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    if (videosArmed) return;
    const el = videosRef.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setVideosArmed(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVideosArmed(true);
          io.disconnect();
        }
      },
      { rootMargin: '600px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [videosArmed]);

  const markReelReady = (i: number, el: HTMLVideoElement) => {
    el.play().catch(() => {});
    setReadyReels((prev) => {
      if (prev.has(i)) return prev;
      const next = new Set(prev);
      next.add(i);
      return next;
    });
  };

  // iOS/Android не начинают качать видео по preload — данные едут только
  // после play(). Поэтому после arm пинаем play() у всех роликов явно
  // (muted выставляем свойством: React не рендерит атрибут muted в DOM).
  // Если автоплей запрещён (Low Power Mode и т.п.) — остаётся постер.
  useEffect(() => {
    if (!videosArmed) return;
    const root = videosRef.current;
    if (!root) return;
    const vids = Array.from(root.querySelectorAll<HTMLVideoElement>('video'));
    for (const v of vids) {
      v.muted = true;
      const p = v.play();
      if (p) p.catch(() => {});
    }
  }, [videosArmed]);

  // Esc закрывает order-модалку.
  useEffect(() => {
    if (!orderModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOrderModalOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [orderModalOpen]);

  // Close burger menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    const id = setInterval(() => {
      setOccIdx((i) => (i + 1) % ctaOccasionKeys.length);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  // On `ask.lovestoria.org` this build is a landing-only mirror — no
  // checkout, no /forms. Order CTAs open the Telegram channel (per the
  // mirror's spec — see ARCHITECTURE.md), and links that would normally
  // route inside the app point at the main domain instead.
  const askVariant = isAskVariant();
  const loginHref = askVariant ? mainAppUrl('/login') : '/login';
  const termsPrivacyHref = askVariant ? mainAppUrl('/terms#privacy') : '/terms#privacy';
  const termsOfferHref = askVariant ? mainAppUrl('/terms#offer') : '/terms#offer';

  const goCheckout = () => {
    // Lead тут не шлём: открытие модалки — ещё не заявка. Событие уходит
    // только с кнопок перехода к менеджеру (см. trackLead ниже).
    // Этот бранч везде использует order-модалку со ссылкой на Telegram.
    // Прежняя ветка с Stripe CheckoutSidebar здесь не нужна — она оставлена
    // лишь для main-домена в других бранчах.
    setOrderModalOpen(true);
  };

  return (
    <main className="lp">
      <header className="lp-top">
        <nav className="lp-nav" ref={navRef}>
          {/* Лого + wordmark сидят в одном контейнере __left, чтобы
              flex space-between гарантированно прижал их к левому краю,
              а правый блок — к правому. */}
          <div className="lp-nav__left">
            <a className="lp-nav__logo" href={langPrefix} aria-label="lovestoria">
              <span
                className="lp-nav__logo-img"
                style={{
                  WebkitMaskImage: `url(${logoSvg})`,
                  maskImage: `url(${logoSvg})`,
                }}
                aria-hidden="true"
              />
            </a>
            <a className="lp-nav__wordmark" href={langPrefix}>lovestoria</a>
          </div>

          <div className="lp-nav__right">
            <LangPicker />
            <button
              type="button"
              className="lp-nav__btn lp-nav__btn--accent"
              onClick={goCheckout}
            >
              {t('nav.menuOrder')}
            </button>
          </div>
        </nav>
      </header>

      <section className="lp-hero">
        {/* Видео first — во всю ширину секции, без скруглений. Под ним
            «карточка» с копией, которая отрицательным margin-top'ом
            заступает на нижнюю кромку видео и имеет border-radius
            сверху. */}
        <video
          className="lp-hero__video"
          autoPlay
          muted
          loop
          playsInline
          poster={heroVideoPoster}
          preload="metadata"
          src={heroVideo}
        />

        <div className="lp-hero__copy-card">
          <div className="lp-hero__copy">
            <h1 className="lp-hero__title">{t('hero.title')}</h1>
            <ul className="lp-hero__pills" aria-hidden="true">
              {t('hero.subtitle')
                .split('·')
                .map((part, i) => {
                  const text = part.trim();
                  if (!text) return null;
                  return (
                    <li key={i} className="lp-hero__pill">
                      {text}
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      </section>

      {/* Три сегмента: романтика / память / коллективно. Каждый блок —
          картинка получателя + заголовок + описание. Стороны чередуются. */}
      <section className="lp-segments">
        <div className="lp-segments__inner">
          

          <article className="lp-segment">
            <div className="lp-segment__media">
              <div className="lp-segment__collage">
                <span className="lp-segment__photo lp-segment__photo--1">
                  <img src={p11} alt="" loading="lazy" draggable={false} />
                </span>
                <span className="lp-segment__photo lp-segment__photo--2">
                  <img src={npGirlfriend} alt="" loading="lazy" draggable={false} />
                </span>
                <span className="lp-segment__photo lp-segment__photo--3">
                  <img src={npBoyfriend} alt="" loading="lazy" draggable={false} />
                </span>
              </div>
            </div>
            <div className="lp-segment__text">
              <h3 className="lp-segment__title">{t('segments.s1Title')}</h3>
              <p className="lp-segment__body">{t('segments.s1Body')}</p>
            </div>
          </article>

          <article className="lp-segment lp-segment--reverse">
            <div className="lp-segment__media">
              <div className="lp-segment__collage">
                <span className="lp-segment__photo lp-segment__photo--1">
                  <img src={npMom} alt="" loading="lazy" draggable={false} />
                </span>
                <span className="lp-segment__photo lp-segment__photo--2">
                  <img src={npGrandma} alt="" loading="lazy" draggable={false} />
                </span>
                <span className="lp-segment__photo lp-segment__photo--3">
                  <img src={npSister} alt="" loading="lazy" draggable={false} />
                </span>
              </div>
            </div>
            <div className="lp-segment__text">
              <h3 className="lp-segment__title">{t('segments.s2Title')}</h3>
              <p className="lp-segment__body">{t('segments.s2Body')}</p>
            </div>
          </article>

          <article className="lp-segment">
            <div className="lp-segment__media">
              <div className="lp-segment__collage">
                <span className="lp-segment__photo lp-segment__photo--1">
                  <img src={npFriend} alt="" loading="lazy" draggable={false} />
                </span>
                <span className="lp-segment__photo lp-segment__photo--2">
                  <img src={npWife} alt="" loading="lazy" draggable={false} />
                </span>
                <span className="lp-segment__photo lp-segment__photo--3">
                  <img src={npHusband} alt="" loading="lazy" draggable={false} />
                </span>
              </div>
            </div>
            <div className="lp-segment__text">
              <h3 className="lp-segment__title">{t('segments.s3Title')}</h3>
              <p className="lp-segment__body">{t('segments.s3Body')}</p>
            </div>
          </article>
        </div>
      </section>

      {/* Видео-отзывы (с aa): вертикальные ролики 9:16 бегущей лентой, без
          звука на повторе. Клик по карточке — переход на Instagram-reel,
          в правом нижнем углу — стрелка-иконка. */}
      <section className="lp-videos" ref={videosRef}>
        <header className="lp-videos__head">
          <h2 className="lp-videos__title">{t('videos.title')}</h2>
        </header>

        <div className="lp-videos__rail">
          <div className="lp-videos__track">
            {[...videoReels, ...videoReels].map((r, i) => (
              <a
                key={i}
                className={'lp-vid' + (readyReels.has(i) ? ' lp-vid--ready' : '')}
                href={r.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram Reel"
              >
                <video
                  className="lp-vid__video"
                  src={videosArmed ? r.src : undefined}
                  poster={r.poster}
                  muted
                  loop
                  playsInline
                  preload={videosArmed ? 'auto' : 'none'}
                  onLoadedData={(e) => markReelReady(i, e.currentTarget)}
                  onCanPlay={(e) => markReelReady(i, e.currentTarget)}
                  onPlaying={(e) => markReelReady(i, e.currentTarget)}
                />
                <span className="lp-vid__arrow" aria-hidden="true">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M7 17 17 7" />
                    <path d="M9 7h8v8" />
                  </svg>
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-how">
        <div className="lp-how__inner">
          <header className="lp-how__head">
            <h2 className="lp-how__title">{t('how.title')}</h2>
          </header>

          <ol className="lp-how__steps">
            <li className="lp-how__step">
              <div className="lp-how__step-text">
                <span className="lp-how__num">01</span>
                <h3 className="lp-how__step-title">{t('how.step1Title')}</h3>
                <p className="lp-how__step-body">{t('how.step1Body')}</p>
              </div>

              <div className="lp-how__asset lp-how__asset--cabinet">
                <CabinetPreviewFrame scenario="answers" lang={cabinetPreviewLangForPage(i18n.language)} title={t('how.step1Title')} />
              </div>
            </li>

            <li className="lp-how__step">
              <div className="lp-how__step-text">
                <span className="lp-how__num">02</span>
                <h3 className="lp-how__step-title">{t('how.step2Title')}</h3>
                <p className="lp-how__step-body">{t('how.step2Body')}</p>
              </div>

              <div className="lp-how__asset lp-how__asset--cabinet">
                <CabinetPreviewFrame scenario="cover" lang={cabinetPreviewLangForPage(i18n.language)} title={t('how.step2Title')} />
              </div>
            </li>

            <li className="lp-how__step">
              <div className="lp-how__step-text">
                <span className="lp-how__num">03</span>
                <h3 className="lp-how__step-title">{t('how.step3Title')}</h3>
                <p className="lp-how__step-body">{t('how.step3Body')}</p>
              </div>

              <div className="lp-how__asset lp-how__asset--photo">
                <div className="lp-how__photo-wrap">
                  <img src={p11} alt="" />
                </div>
                <div className="lp-how__package" aria-hidden="true">
                  <img src={packageImage} alt="" />
                </div>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* «Книга изнутри» (с aa) — карусель из 5 квадратных разворотов:
          обложка → титул → оглавление → интервью → фото. Сверху
          объясняющий текст (анимируется при смене), на сцене две
          круглые стрелки. */}
      <section className="lp-inner" id="lp-inner">
        <div className="lp-inner__inner">
          <header className="lp-inner__head">
            <h2 className="lp-inner__title">{t('inner.title')}</h2>
          </header>

          <div className="lp-inner__viewer">
            <div className="lp-inner__copy" key={`copy-${innerIdx}`}>
              <h3 className="lp-inner__cap-title">
                {t(innerSlides[innerIdx].titleKey)}
              </h3>
              <p className="lp-inner__cap-body">
                {t(innerSlides[innerIdx].bodyKey)}
              </p>
            </div>

            <div className="lp-inner__stage">
              {innerSlides.map((s, i) => (
                <img
                  key={i}
                  className={
                    'lp-inner__img' +
                    (i === innerIdx ? ' lp-inner__img--active' : '')
                  }
                  src={s.src}
                  alt=""
                  loading="lazy"
                  draggable={false}
                />
              ))}

              <div className="lp-inner__arrows">
                <button
                  type="button"
                  className="lp-inner__arrow"
                  aria-label={t('inner.prev')}
                  onClick={innerPrev}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M9 1L3 7l6 6" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="lp-inner__arrow"
                  aria-label={t('inner.next')}
                  onClick={innerNext}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                    <path d="M5 1l6 6-6 6" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            <button
              type="button"
              className="lp-inner__cta"
              onClick={goCheckout}
            >
              <span>{t('info.ctaPrimary')}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="lp-reviews">
        <header className="lp-reviews__head">
          <h2 className="lp-reviews__title">{t('reviews.title')}</h2>
          <p className="lp-reviews__subtitle">{t('reviews.subtitle')}</p>
        </header>

        {/* Две вертикальные колонны: левая едет вверх, правая — вниз.
            Каждая карточка получает чередующуюся высоту через
            nth-child, поэтому колонны выглядят неоднородно. */}
        <div className="lp-reviews__cols">
          {[0, 1, 2].map((colIdx) => {
            const slots = reviewSlots.filter((_, i) => i % 3 === colIdx);
            const dir = colIdx === 1 ? 'down' : 'up';
            return (
              <div
                key={colIdx}
                className={
                  'lp-reviews__col ' +
                  (dir === 'up' ? 'lp-reviews__col--up' : 'lp-reviews__col--down')
                }
              >
                <div className="lp-reviews__col-track">
                  {[...slots, ...slots, ...slots].map((r, i) =>
                    r.kind === 'photo' ? (
                      <article key={i} className="lp-rev lp-rev--photo">
                        <img
                          className="lp-rev__photo"
                          src={r.poster}
                          alt=""
                          loading="lazy"
                          draggable={false}
                        />
                      </article>
                    ) : (
                      <article key={i} className="lp-rev lp-rev--text">
                        <p className="lp-rev__quote">«{t(r.quoteKey)}»</p>
                        <p className="lp-rev__by">— {t(r.byKey)}</p>
                      </article>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ — три раскрывающихся пункта. Первый — таймминги (раньше жили
          отдельным блоком в lp-how). Остальные — статичные ответы. */}
      <section className="lp-faq" id="lp-faq">
        <div className="lp-faq__inner">
          <h2 className="lp-faq__title">{t('faq.title')}</h2>
          <div className="lp-faq__list">
            <details className="lp-faq__item" open>
              <summary className="lp-faq__q">
                <span>{t('faq.q1Title')}</span>
                <span className="lp-faq__arrow" aria-hidden="true">+</span>
              </summary>
              <div className="lp-faq__a">
                <ul className="lp-faq__timing-list">
                  <li>
                    <span>{t('how.timingEdit')}</span>
                    <span className="lp-faq__timing-value">{t('how.timingEditValue')}</span>
                  </li>
                  <li>
                    <span>{t('how.timingDelivery')}</span>
                    <span className="lp-faq__timing-value">{t('how.timingDeliveryValue')}</span>
                  </li>
                </ul>
                <div className="lp-faq__timing-total">
                  <span>{t('how.timingTotal')}</span>
                  <span className="lp-faq__timing-value">{t('how.timingTotalValue')}</span>
                </div>
                <p className="lp-faq__note">{t('how.timingNote')}</p>
              </div>
            </details>

            <details className="lp-faq__item">
              <summary className="lp-faq__q">
                <span>{t('faq.q2Title')}</span>
                <span className="lp-faq__arrow" aria-hidden="true">+</span>
              </summary>
              <div className="lp-faq__a">
                {t('faq.q2Body').split('\n').filter(Boolean).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </details>

            <details className="lp-faq__item">
              <summary className="lp-faq__q">
                <span>{t('faq.q3Title')}</span>
                <span className="lp-faq__arrow" aria-hidden="true">+</span>
              </summary>
              <div className="lp-faq__a">
                {t('faq.q3Body').split('\n').filter(Boolean).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </details>
          </div>
        </div>
      </section>

      <section className="lp-cta">
        <div className="lp-cta__inner">
          <div className="lp-cta__head">
            <h2 className="lp-cta__title">
              {t('cta.titlePrefix')}<br />
              <span className="lp-cta__cycle" key={`w${occIdx}`}>
                {t(ctaOccasionKeys[occIdx].wordKey)}
              </span>
            </h2>
            {/* Книга в lp-cta — без 3D-анимации, чисто плоская передняя
                обложка. Сменяется вместе с occIdx. */}
            <div className="lp-cta__book">
              <BookCover
                flat
                frozen
                hideSpine
                background={bookCovers[occIdx % bookCovers.length]}
              />
            </div>
          </div>
          <div className="lp-cta__actions">
            <div className="lp-cta__btns">
              <a
                className="lp-cta__btn lp-cta__btn--telegram"
                href={TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackLead('cta_telegram')}
              >
                <TelegramIcon />
                <span>{t('order.telegram')}</span>
              </a>
            </div>
            <p className="lp-cta__note">{t('cta.note')}</p>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer__inner lp-footer__inner--legal">
          <span className="lp-footer__copy">{t('footer.copyright')}</span>
          <nav className="lp-footer__legal-links">
            <a href={termsPrivacyHref}>{t('footer.legalPrivacy')}</a>
            <a href={termsOfferHref}>{t('footer.legalTerms')}</a>
          </nav>
        </div>
      </footer>

      {/* Floating order CTA — всегда зафиксирован снизу с зазором 20px. */}
      <button
        type="button"
        className="lp-hero__caption-cta lp-hero__caption-cta--floating"
        onClick={goCheckout}
      >
        {t('stickycta.order')} Lovestoria™
      </button>

      {/* Order-модалка со ссылкой на Telegram. Появляется по любому CTA «Заказать»:
          на десктопе — центрированный попап, на мобиле — нижний модал. */}
      {orderModalOpen && (
        <div
          className="lp-order"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-order-title"
          onClick={() => setOrderModalOpen(false)}
        >
          <div
            className="lp-order__panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="lp-order__close"
              aria-label={t('order.close')}
              onClick={() => setOrderModalOpen(false)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M1 1l12 12M13 1L1 13"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <header className="lp-order__head">
              <h3 id="lp-order-title" className="lp-order__title">
                {t('order.title')}
              </h3>
              <p className="lp-order__subtitle">{t('order.subtitle')}</p>
            </header>

            <div className="lp-order__btns">
              <a
                className="lp-order__btn lp-order__btn--telegram"
                href={TELEGRAM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackLead('modal_telegram');
                  setOrderModalOpen(false);
                }}
              >
                <TelegramIcon />
                <span>{t('order.telegram')}</span>
              </a>
              <p className="lp-order__eta">{t('order.eta')}</p>
            </div>

            {/* Принимаемые карты — Visa, Mastercard, МИР. Inline-SVG,
                без внешних ассетов: чип-рамка с брендовым цветом + лого. */}
            <div className="lp-order__cards" aria-label="Stripe, Visa, Mastercard, МИР">
              <span className="lp-order__card lp-order__card--stripe">
                <svg viewBox="0 0 60 38" aria-hidden="true">
                  <rect width="60" height="38" rx="5" fill="#635BFF" />
                  <text
                    x="30"
                    y="24"
                    fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
                    fontSize="11"
                    fontWeight="700"
                    fill="#fff"
                    textAnchor="middle"
                    letterSpacing="0"
                  >
                    stripe
                  </text>
                </svg>
              </span>
              <span className="lp-order__card lp-order__card--visa">
                <svg viewBox="0 0 60 38" aria-hidden="true">
                  <rect width="60" height="38" rx="5" fill="#1A1F71" />
                  <text
                    x="30"
                    y="25"
                    fontFamily="'Arial Black', Arial, sans-serif"
                    fontSize="14"
                    fontWeight="900"
                    fontStyle="italic"
                    fill="#fff"
                    textAnchor="middle"
                    letterSpacing="0.6"
                  >
                    VISA
                  </text>
                </svg>
              </span>
              <span className="lp-order__card lp-order__card--mc">
                <svg viewBox="0 0 60 38" aria-hidden="true">
                  <rect
                    width="60"
                    height="38"
                    rx="5"
                    fill="#fff"
                    stroke="#e5e3dc"
                    strokeWidth="1"
                  />
                  <circle cx="24" cy="19" r="9" fill="#EB001B" />
                  <circle cx="36" cy="19" r="9" fill="#F79E1B" fillOpacity="0.85" />
                </svg>
              </span>
              <span className="lp-order__card lp-order__card--mir">
                <svg viewBox="0 0 60 38" aria-hidden="true">
                  <rect width="60" height="38" rx="5" fill="#0F754E" />
                  <text
                    x="30"
                    y="24"
                    fontFamily="'Arial Black', Arial, sans-serif"
                    fontSize="13"
                    fontWeight="900"
                    fill="#fff"
                    textAnchor="middle"
                    letterSpacing="0.6"
                  >
                    МИР
                  </text>
                </svg>
              </span>
            </div>

            {/* FAQ-кнопка — закрывает модалку и плавно скроллит к секции
                #lp-faq. Элемент существует независимо от модалки, поэтому
                scrollIntoView сразу же находит цель. */}
            <button
              type="button"
              className="lp-order__faq"
              onClick={() => {
                setOrderModalOpen(false);
                document
                  .getElementById('lp-faq')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {t('order.faq')}
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default Landing;
